<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { DocumentDuplicateIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'
import CrdRange from "@/components/common/CrdRange.vue"
import { useForm } from "vee-validate"
import copy from 'copy-to-clipboard'
import * as yup from "yup"
import { useToast } from 'vue-toastification'
import { API_WIDGET_URL, DEFAULT_ENVIRONMENT, getWidgetUrlByEnv } from '@/config/env.const.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import { WIDGET_ASSISTANT_IFRAME_DESKTOP, WIDGET_ASSISTANT_IFRAME_DESKTOP_INTEGRATION } from '@/config/visual.const.ts'
import CrdWrapper from '@/components/common/CrdWrapper.vue'
import CrdBlock from '@/components/common/CrdBlock.vue'
import { useUserStore } from '@/stores/user.store.ts'
import { useProjectSettingStore } from '@/stores/project-settings.store.ts'
import { storeToRefs } from 'pinia'
import { ButtonType, ButtonVariant } from '@/components/common/crd-button/crd-button.enums.ts'
import CrdButton from '@/components/common/crd-button/CrdButton.vue'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/20/solid'

enum FormField {
  CollapseIfWidthLessThanPx = 'collapseIfWidthLessThanPx',
  DeveloperMode = 'isDeveloperMode',
  DeveloperModeRefererUrl = 'developerModeRefererUrl',
  AutoOpenDelayMs = 'autoOpenDelayMs',
}

const { t } = useTranslations()
const toast = useToast()
const emit = defineEmits(['reset'])
const props = defineProps<{
  projectKey: string
}>()
const userStore = useUserStore()
const projectSettingsStore = useProjectSettingStore()
const { projectSelected } = storeToRefs(userStore)
const { integrationIframeRef, isWidgetRedrawRequired } = storeToRefs(projectSettingsStore)
const { values } = useForm< {
  autoOpenDelayMs: number
  collapseIfWidthLessThanPx: number
}>({
  validationSchema: yup.object().shape({
    [FormField.CollapseIfWidthLessThanPx]: yup.number().integer().required(),
    [FormField.AutoOpenDelayMs]: yup.number().integer().required(),
  }),
  initialValues: {
    [FormField.CollapseIfWidthLessThanPx]: 640,
    [FormField.AutoOpenDelayMs]: 2500
  }
})

const scriptUrl = computed<string>(() => {
  const url = getWidgetUrlByEnv(DEFAULT_ENVIRONMENT)
  return `<script src="${url}/widget.js" crowdy-project-key="${props.projectKey}" auto-open-delay-ms="${values.autoOpenDelayMs}" collapsed-if-width-less="${values.collapseIfWidthLessThanPx}" async><\/script>`
})


</script>

<template>
  <div class="container mx-auto">
    <div class="flex flex-col md:flex-row gap-4">
      <div class="p-6 rounded-lg flex-2 text-black bg-opacity-90">

            <CrdBlock class="flex-col text-black">
              <div class="flex">
                <div class="flex flex-col w-full justify-center">
                  <h2 class="text-base font-semibold text-center">Скопируйте код и установите на сайт</h2>
                </div>
                <div class="flex">
                  <div
                    @click="() => {
                      copy(scriptUrl)
                      toast.success(t('howToIntegrateModal.copied'))
                    }"
                    class="mx-auto flex size-12 items-center justify-center rounded-full bg-green-100 hover:bg-green-200 hover:cursor-pointer"
                  >
                    <DocumentDuplicateIcon class="size-6 hover:size-7 text-green-600" aria-hidden="true" />
                  </div>
                </div>
              </div>
              <div class="flex-col bg-gray-300 text-sm rounded-md p-4 text-black bg-white ignore-crd-block-click-event">
                <code class="text-left">{{ scriptUrl }}</code>
              </div>
            </CrdBlock>

            <CrdBlock class="text-black mt-5">
              <CrdRange
                :label="t('howToIntegrateModal.collapseIfScreenWidthLessThan')"
                :name="FormField.CollapseIfWidthLessThanPx"
                :min="320"
                :max="1024"
                :step="1"
                unit="px"
              />
            </CrdBlock>
            <CrdBlock class="text-black mt-5 p-2">
              <CrdRange
                :label="t('howToIntegrateModal.widgetAppearanceDelay')"
                :name="FormField.AutoOpenDelayMs"
                :min="0"
                :max="10000"
                :step="100"
                unit="ms"
              />
            </CrdBlock>
        <!--<div class="mt-5 sm:mt-5">
          <div class="flex w-full">
            <div class="flex flex-col">
              <div class="mx-auto flex size-12 items-center justify-center rounded-full bg-green-100">
                <QuestionMarkCircleIcon class="size-8 text-green-600" aria-hidden="true" />
              </div>
            </div>
            <div class="flex flex-col w-full justify-center">
              <h2 class="text-base font-semibold text-center">Что будет после установки?</h2>
            </div>
          </div>
          <br/>
          <p>После установки виджет приступит к работе. Будет вступать в контакт с каждым посетителем, собирать вопросы и контактные данные.</p>
        </div>-->
      </div>
      <div class="p-6 rounded-lg flex-1 bg-opacity-90 justify-center hidden lg:block">
        <iframe
          name="crowdy-ai-iframe-integration"
          :id="WIDGET_ASSISTANT_IFRAME_DESKTOP_INTEGRATION"
          :src="`${API_WIDGET_URL}?projectKey=${projectKey}&themeSelectionMode=true&referrer=aHR0cHM6Ly9jcm93ZHkuYWk=`"
          class="crowdy-ai-iframe rounded-xl h-full max-h-[500px] w-[calc(40vw)] min-w-[300px] max-w-[360px] z-10 inline-block m-auto"
          :crowdy-project-key="projectKey"
          auto-open-delay-ms="2500"
          collapsed-if-width-less="640"
          frameborder="0"
          allow="camera; microphone; autoplay;"
          ref="integrationIframeRef"
          @load="() => {
            isWidgetRedrawRequired = true
          }"
        />

        <div class="mt-3 flex items-center justify-center gap-x-3">
          <CrdButton
            class="px-4 py-2 bg-indigo-200 text-indigo-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
            :variant="ButtonVariant.Save"
            :type="ButtonType.Submit"
          >
            <CheckCircleIcon class="block size-5" aria-hidden="true" />
            <span>{{ t('common.buttons.save') }}</span>
          </CrdButton>
          <CrdButton
            class="px-4 py-2 bg-indigo-200 text-indigo-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
            :variant="ButtonVariant.Cancel"
            @click="emit('reset')"
          >
            <XCircleIcon class="block size-5" aria-hidden="true" />
            {{ t('common.buttons.cancel') }}
          </CrdButton>
        </div>
      </div>
    </div>
  </div>
</template>
